import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { track } from "@/lib/track";

const VIDEO_SRC = "/puertas-olimpo.mp4";

// Efecto máquina de escribir por párrafo (30ms/char, 700ms entre párrafos). Igual a los momentos.
function Typewriter({ parrafos, onDone }) {
  const [idx, setIdx] = useState(0);
  const [texto, setTexto] = useState("");
  useEffect(() => {
    if (idx >= parrafos.length) { onDone && onDone(); return; }
    const full = parrafos[idx];
    let i = 0;
    setTexto("");
    const tick = setInterval(() => {
      i++;
      setTexto(full.slice(0, i));
      if (i >= full.length) {
        clearInterval(tick);
        setTimeout(() => setIdx((n) => n + 1), 700);
      }
    }, 30);
    return () => clearInterval(tick);
  }, [idx]); // eslint-disable-line
  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {parrafos.slice(0, idx).map((p, i) => (
        <p key={i} className="font-serif text-crema" style={{ fontSize: "clamp(20px,2.4vw,28px)", lineHeight: 1.4 }}>{p}</p>
      ))}
      {idx < parrafos.length && (
        <p className="font-serif text-crema" style={{ fontSize: "clamp(20px,2.4vw,28px)", lineHeight: 1.4 }}>
          {texto}<span className="text-dorado animate-pulse">|</span>
        </p>
      )}
    </div>
  );
}

// LAS PUERTAS DEL OLIMPO: recompensa a pantalla completa (video mudo -> texto -> SEGUIR).
export default function PuertasScreen({ data, onSeguir }) {
  const [fase, setFase] = useState("cargando"); // cargando | video | texto
  const [conSonido, setConSonido] = useState(false);
  const [mostrarSaltear, setMostrarSaltear] = useState(false);
  const [textoListo, setTextoListo] = useState(false);
  const videoRef = useRef(null);
  const nombre = data.nombre || "Che";
  const horas = data.horas ?? 0;
  const procesos = data.procesos ?? 0;
  const parrafos = [
    `${nombre}.`,
    `${procesos} procesos andando. ${horas} horas por mes que antes no existían.`,
    "La mayoría de las empresas que compran inteligencia artificial no llega nunca a implementar una sola cosa. Compran la herramienta, la miran, y ahí queda.",
    `Vos llevás ${procesos} andando.`,
    "Sos parte de un grupo bastante más chico de lo que imaginás.",
    "Y esto recién se está poniendo bueno.",
  ];

  // Precarga: si el video llega, lo mostramos; si no en 4s, vamos directo al texto.
  useEffect(() => {
    const v = document.createElement("video");
    v.preload = "auto";
    v.muted = true;
    v.src = VIDEO_SRC;
    let listo = false;
    const ok = () => { if (!listo) { listo = true; setFase("video"); } };
    const fail = () => { if (!listo) { listo = true; setFase("texto"); } };
    v.addEventListener("canplay", ok);
    v.addEventListener("loadeddata", ok);
    v.addEventListener("error", fail);
    try { v.load(); } catch { /* noop */ }
    const to = setTimeout(fail, 4000);
    return () => {
      clearTimeout(to);
      v.removeEventListener("canplay", ok);
      v.removeEventListener("loadeddata", ok);
      v.removeEventListener("error", fail);
    };
  }, []);

  useEffect(() => {
    if (fase === "video") {
      const t = setTimeout(() => setMostrarSaltear(true), 3000);
      const v = videoRef.current;
      if (v) { v.muted = true; v.play().catch(() => {}); }
      return () => clearTimeout(t);
    }
  }, [fase]);

  const activarSonido = () => {
    const v = videoRef.current;
    if (v) { v.muted = false; v.volume = 1; }
    setConSonido(true);
    track("puertas_sonido_activado", {});
  };

  const saltear = () => { track("puertas_salteado", {}); setFase("texto"); };
  const videoTermino = () => { track("puertas_visto_entero", {}); setFase("texto"); };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden"
      style={{ background: "#0A0A0A" }}
      data-testid="puertas-screen"
    >
      <AnimatePresence mode="wait">
        {fase === "video" && (
          <motion.div key="video" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0">
            <video
              ref={videoRef}
              src={VIDEO_SRC}
              className="w-full h-full object-cover"
              playsInline
              muted
              onEnded={videoTermino}
              data-testid="puertas-video"
            />
            {/* Activar sonido */}
            {!conSonido && (
              <button
                onClick={activarSonido}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 px-5 py-2.5 rounded-full backdrop-blur-md transition-colors"
                style={{ background: "rgba(0,0,0,0.55)", border: "1px solid rgba(201,168,76,0.5)", color: "#F5F0E8" }}
                data-testid="puertas-activar-sonido"
              >
                <VolumeX size={16} /> <span className="text-sm">Activar sonido</span>
              </button>
            )}
            {conSonido && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: "rgba(0,0,0,0.4)", color: "#8A8A8A" }}>
                <Volume2 size={15} /> <span className="text-xs">Sonido activado</span>
              </div>
            )}
            {mostrarSaltear && (
              <button onClick={saltear} className="absolute top-6 right-6 text-xs px-3 py-1.5 rounded-full transition-colors"
                style={{ color: "#9A9A9A", background: "rgba(0,0,0,0.4)" }} data-testid="puertas-saltear">
                saltear
              </button>
            )}
          </motion.div>
        )}

        {fase === "texto" && (
          <motion.div key="texto" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
            className="px-6 text-center w-full" data-testid="puertas-texto">
            <div className="mono-label text-dorado mb-8" style={{ letterSpacing: "0.3em", fontSize: 11 }}>LAS PUERTAS DEL OLIMPO</div>
            <Typewriter parrafos={parrafos} onDone={() => setTextoListo(true)} />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: textoListo ? 1 : 0 }} transition={{ duration: 0.6 }} className="mt-12">
              <button
                onClick={() => { onSeguir && onSeguir(); }}
                disabled={!textoListo}
                className="btn-dorado px-10"
                data-testid="puertas-seguir"
              >
                SEGUIR
              </button>
            </motion.div>
          </motion.div>
        )}

        {fase === "cargando" && (
          <motion.div key="cargando" className="text-gris text-sm" data-testid="puertas-cargando">…</motion.div>
        )}
      </AnimatePresence>
    </motion.div>,
    document.body
  );
}
